import { useEffect, useState, type FormEvent } from 'react';
import { useParams } from 'react-router';
import { ApiError, api } from '../api/client';
import type { EgenOppforing, Leverandor } from '../api/types';
import { Leverandorannonse, type Annonseinnhold } from '../components/Leverandorannonse';
import { Leverandorkart } from '../components/Leverandorkart';
import { Leverandorkort } from '../components/Leverandorkort';
import { Alert, Field, LoadingBlock } from '../components/ui';
import type { Kartoppsett } from '../lib/googlekart';
import { useResource } from '../hooks/useResource';

/**
 * Sida et lokale får gjennom lenken i henvendelsen.
 *
 * Ingen konto, ingen innlogging: lenken er hele adgangen, og den virker til
 * den går ut. Det de legger inn her, er det brudeparene ser – så sida sier
 * det rett ut, og viser bildene slik de vil stå.
 *
 * Kategorien og publiseringen er ikke deres å endre. Kategorien er aksen
 * registeret sorterer langs, og om oppføringen skal ligge ute, avgjør vi
 * etter å ha sett hva som kom inn. Sida sier fra om hvilken av delene som
 * gjelder, så ingen lurer på hvorfor «lagre» ikke gjorde noe synlig.
 */

type Svar = {
  oppforing: EgenOppforing;
  henvendelse: { utloper: string; fra: string | null; besvart: boolean };
  kart: Kartoppsett;
};

const MAKS_BILDER = 12;

/**
 * Id-en forhåndsvisningen bruker.
 *
 * Kortet og kartet er bygget for en liste med flere, og trenger noe å skille
 * dem med. Her er det bare én, og den ekte id-en har lokalet ingen bruk for –
 * de kommer aldri til å slå den opp.
 */
const FORHANDSVISNING = 'forhandsvisning';

function dato(iso: string): string {
  return new Date(iso).toLocaleDateString('nb-NO', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

export function OppforingPage() {
  const { token = '' } = useParams();
  const { data, loading, error, setData } = useResource<Svar>(
    () => api.get<Svar>(`/leverandorhenvendelse/${token}`),
    [token],
  );

  const [form, setForm] = useState<EgenOppforing | null>(null);
  const [lagrer, setLagrer] = useState(false);
  const [lasterOpp, setLasterOpp] = useState(false);
  const [melding, setMelding] = useState<{ slag: 'success' | 'error'; tekst: string } | null>(null);
  const [feltfeil, setFeltfeil] = useState<ApiError | null>(null);
  const [modus, setModus] = useState<'rediger' | 'forhandsvis'>('rediger');
  const [visning, setVisning] = useState<'annonse' | 'kart' | 'liste'>('annonse');
  const [valgtPaaKart, setValgtPaaKart] = useState<string | null>(FORHANDSVISNING);

  // Skjemaet fylles én gang, fra det som ligger lagret. Etter det er det
  // skjemaet som gjelder, til man lagrer.
  useEffect(() => {
    if (data && form === null) setForm(data.oppforing);
  }, [data, form]);

  if (loading) {
    return (
      <div className="rsvp-page">
        <div className="rsvp-card oppforing-card">
          <LoadingBlock label="Henter oppføringen …" />
        </div>
      </div>
    );
  }

  if (error || !data || !form) {
    return (
      <div className="rsvp-page">
        <div className="rsvp-card">
          <h1 style={{ fontSize: '1.6rem' }}>Denne lenken virker ikke</h1>
          <p className="muted">
            {error?.message ?? 'Fant ikke oppføringen.'} Lenker av denne typen virker i tretti
            dager. Svar på e-posten dere fikk, så sender vi en ny.
          </p>
        </div>
      </div>
    );
  }

  const sett = <K extends keyof EgenOppforing>(felt: K, verdi: EgenOppforing[K]) =>
    setForm((f) => (f ? { ...f, [felt]: verdi } : f));

  const lagre = async (e: FormEvent) => {
    e.preventDefault();
    setLagrer(true);
    setMelding(null);
    setFeltfeil(null);
    try {
      const svar = await api.put<Svar>(`/leverandorhenvendelse/${token}`, {
        navn: form.navn,
        kortBeskrivelse: form.kortBeskrivelse,
        beskrivelse: form.beskrivelse,
        adresse: form.adresse,
        postnummer: form.postnummer,
        poststed: form.poststed,
        nettsted: form.nettsted,
        epost: form.epost,
        telefon: form.telefon,
        prisFra: form.prisFra,
      });
      setData(svar);
      setForm(svar.oppforing);
      setMelding({
        slag: 'success',
        tekst: svar.oppforing.publisert
          ? 'Lagret. Endringene vises i registeret med én gang.'
          : 'Lagret. Vår Dag publiserer oppføringen når den er gjennomgått.',
      });
    } catch (caught) {
      if (caught instanceof ApiError) setFeltfeil(caught);
      setMelding({
        slag: 'error',
        tekst: caught instanceof ApiError ? caught.message : 'Klarte ikke å lagre.',
      });
    } finally {
      setLagrer(false);
    }
  };

  const oppdaterBilder = (bilder: string[]) => {
    setForm((f) => (f ? { ...f, bilder } : f));
    setData((d) => ({ ...d, oppforing: { ...d.oppforing, bilder } }));
  };

  const lastOpp = async (fil: File) => {
    setMelding(null);
    setLasterOpp(true);
    try {
      const dataUrl = await new Promise<string>((los, avvis) => {
        const leser = new FileReader();
        leser.onload = () => los(String(leser.result));
        leser.onerror = () => avvis(new Error('lesefeil'));
        leser.readAsDataURL(fil);
      });
      const svar = await api.post<{ bilder: string[] }>(`/leverandorhenvendelse/${token}/bilder`, {
        bilde: dataUrl,
      });
      oppdaterBilder(svar.bilder);
    } catch (caught) {
      setMelding({
        slag: 'error',
        tekst: caught instanceof ApiError ? caught.message : 'Klarte ikke å laste opp bildet.',
      });
    } finally {
      setLasterOpp(false);
    }
  };

  const slettBilde = async (id: string) => {
    const svar = await api.del<{ bilder: string[] }>(`/leverandorhenvendelse/${token}/bilder/${id}`);
    oppdaterBilder(svar.bilder);
  };

  const settForside = async (id: string) => {
    const svar = await api.put<{ bilder: string[] }>(
      `/leverandorhenvendelse/${token}/bilder/${id}/forside`,
      {},
    );
    oppdaterBilder(svar.bilder);
  };

  const fra = data.henvendelse.fra?.split(' ')[0] ?? null;

  /*
    Forhåndsvisningen bygges av skjemaet, ikke av det som ligger lagret.

    Det er hele poenget med å ha den: man skriver en setning, ser hvordan den
    tar seg ut på kortet, og retter. Måtte man lagre først, ville det vært en
    kvittering og ikke en forhåndsvisning.

    Tre ting kommer likevel fra serveren, fordi de ikke står i skjemaet:
    punktet på kartet, stjernene og vurderingene. Punktet følger den *lagrede*
    adressen – nåla flytter seg når de lagrer, ikke mens de skriver – og det
    sier kartvisningen fra om.
  */
  const annonse: Annonseinnhold = {
    navn: form.navn || data.oppforing.navn,
    kategori: form.kategori,
    kortBeskrivelse: form.kortBeskrivelse,
    beskrivelse: form.beskrivelse,
    adresse: form.adresse,
    postnummer: form.postnummer,
    poststed: form.poststed,
    nettsted: form.nettsted,
    epost: form.epost,
    telefon: form.telefon,
    prisFra: form.prisFra,
    snitt: data.oppforing.snitt,
    antallVurderinger: data.oppforing.antallVurderinger,
    bilder: form.bilder,
    vurderinger: data.oppforing.vurderinger,
  };

  const kort: Leverandor = {
    id: FORHANDSVISNING,
    navn: annonse.navn,
    kategori: annonse.kategori,
    kortBeskrivelse: annonse.kortBeskrivelse,
    adresse: annonse.adresse,
    postnummer: annonse.postnummer,
    poststed: annonse.poststed,
    breddegrad: data.oppforing.breddegrad,
    lengdegrad: data.oppforing.lengdegrad,
    nettsted: annonse.nettsted,
    prisFra: annonse.prisFra,
    snitt: annonse.snitt,
    antallVurderinger: annonse.antallVurderinger,
    forsidebilde: form.bilder[0] ?? null,
  };

  const paaKartet = kort.breddegrad !== null && kort.lengdegrad !== null;
  // Adressen i skjemaet er ikke lagret ennå, så nåla står fortsatt der den sto.
  const adresseEndret =
    form.adresse !== data.oppforing.adresse ||
    form.postnummer !== data.oppforing.postnummer ||
    form.poststed !== data.oppforing.poststed;

  return (
    <div className="rsvp-page">
      <div className={`rsvp-card oppforing-card${modus === 'forhandsvis' ? ' bred' : ''}`}>
        <p className="eyebrow">Leverandørregisteret på Vår Dag</p>
        <h1 className="oppforing-tittel">{data.oppforing.navn || 'Oppføringen deres'}</h1>
        <p className="muted">
          Det dere legger inn her, er det brudeparene ser når de leter etter{' '}
          {data.oppforing.kategoriNavn.toLowerCase()}. Lenken virker til {dato(data.henvendelse.utloper)}
          , og kan brukes så mange ganger dere vil.
          {fra ? ` Den ble sendt av ${fra} hos Vår Dag.` : ''}
        </p>

        <div className="modusvelger" role="group" aria-label="Visning">
          <button
            type="button"
            className={`btn btn-sm ${modus === 'rediger' ? 'btn-primary' : 'btn-secondary'}`}
            aria-pressed={modus === 'rediger'}
            onClick={() => setModus('rediger')}
          >
            Rediger
          </button>
          <button
            type="button"
            className={`btn btn-sm ${modus === 'forhandsvis' ? 'btn-primary' : 'btn-secondary'}`}
            aria-pressed={modus === 'forhandsvis'}
            onClick={() => setModus('forhandsvis')}
          >
            Slik ser den ut
          </button>
        </div>

        {modus === 'forhandsvis' ? (
          <div className="forhandsvisning">
            <p className="forhandsvisning-merke">
              Forhåndsvisning · slik brudeparene ser oppføringen deres. Her vises bare deres
              egen. Det dere har skrevet inn, er med selv om dere ikke har lagret ennå.
            </p>

            <div className="visningsvelger" role="group" aria-label="Hvor">
              {(
                [
                  ['annonse', 'Siden deres'],
                  ['kart', 'I kartet'],
                  ['liste', 'I lista'],
                ] as const
              ).map(([verdi, tekst]) => (
                <button
                  key={verdi}
                  type="button"
                  className={`btn btn-sm ${visning === verdi ? 'btn-primary' : 'btn-secondary'}`}
                  aria-pressed={visning === verdi}
                  onClick={() => setVisning(verdi)}
                >
                  {tekst}
                </button>
              ))}
            </div>

            {visning === 'annonse' ? (
              <div className="forhandsvisning-flate">
                <Leverandorannonse data={annonse} />
              </div>
            ) : null}

            {visning === 'kart' ? (
              paaKartet ? (
                <div className="kartvisning">
                  <Leverandorkart
                    leverandorer={[kort]}
                    oppsett={data.kart}
                    valgtId={valgtPaaKart}
                    onVelg={setValgtPaaKart}
                    publikum
                    startpunkt={{
                      bredde: kort.breddegrad!,
                      lengde: kort.lengdegrad!,
                      zoom: 13,
                    }}
                  />
                  {valgtPaaKart ? (
                    <div className="kartkort">
                      <Leverandorkort
                        leverandor={kort}
                        kompakt
                        lenke={false}
                        onLukk={() => setValgtPaaKart(null)}
                      />
                    </div>
                  ) : null}
                </div>
              ) : (
                <p className="varsellinje">
                  Oppføringen har ingen nål på kartet ennå. Fyll inn adressen og lagre, så
                  finner vi punktet – da dukker dere opp i kartet brudeparene leter i.
                </p>
              )
            ) : null}

            {visning === 'kart' && paaKartet && adresseEndret ? (
              <p className="hint">
                Nåla står på adressen slik den er lagret. Lagrer dere adressen dere har
                skrevet inn nå, flytter den seg dit.
              </p>
            ) : null}

            {visning === 'liste' ? (
              <>
                <div className="leverandorliste forhandsvisning-liste">
                  <Leverandorkort leverandor={kort} lenke={false} />
                </div>
                <p className="hint">
                  Slik står dere blant de andre i lista. Det første bildet er det som vises
                  her, og setningen under navnet er «Én setning om stedet».
                </p>
              </>
            ) : null}

            <p className="hjelpetekst">
              {data.oppforing.publisert
                ? 'Oppføringen er publisert, så dette er det brudeparene ser i dag.'
                : 'Oppføringen er ikke publisert ennå. Vår Dag legger den ut når den er fylt ut.'}
            </p>
          </div>
        ) : null}

        <form className="oppforing-skjema" onSubmit={lagre} hidden={modus !== 'rediger'}>
          <h2>Om stedet</h2>

          <Field label="Navn" error={feltfeil?.fieldError('navn')}>
            <input
              className="input"
              value={form.navn}
              required
              maxLength={160}
              onChange={(e) => sett('navn', e.target.value)}
            />
          </Field>

          <Field
            label="Én setning om stedet"
            hint="Vises på kortet i lista og på kartet. Hva slags sted, hvor, og for hvor mange."
            error={feltfeil?.fieldError('kortBeskrivelse')}
          >
            <input
              className="input"
              value={form.kortBeskrivelse}
              maxLength={300}
              onChange={(e) => sett('kortBeskrivelse', e.target.value)}
            />
          </Field>

          <Field
            label="Beskrivelse"
            hint="Teksten i annonsen. Et linjeskift gir et nytt avsnitt."
            error={feltfeil?.fieldError('beskrivelse')}
          >
            <textarea
              className="textarea"
              rows={8}
              value={form.beskrivelse}
              onChange={(e) => sett('beskrivelse', e.target.value)}
            />
          </Field>

          <h2>Bilder</h2>
          <p className="hint">
            Det første bildet er forsiden, det brudeparene ser i lista. Inntil {MAKS_BILDER} bilder,
            hvert på høyst 4 MB. Last bare opp bilder dere selv har rett til å bruke.
          </p>
          {form.bilder.length > 0 ? (
            <ul className="bildekort">
              {form.bilder.map((b, i) => (
                <li key={b} className="bilderute stor">
                  <img src={`/api/leverandorbilder/${b}`} alt={`Bilde ${i + 1}`} />
                  {i === 0 ? <span className="forsidemerke">Forside</span> : null}
                  <div className="bildevalg">
                    {i !== 0 ? (
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        onClick={() => void settForside(b)}
                      >
                        Bruk som forside
                      </button>
                    ) : null}
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      onClick={() => void slettBilde(b)}
                    >
                      Slett
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <p className="tomt">Ingen bilder ennå.</p>
          )}
          {form.bilder.length < MAKS_BILDER ? (
            <label className="btn btn-secondary filvelger">
              {lasterOpp ? 'Laster opp …' : 'Legg til bilde'}
              <input
                type="file"
                accept="image/png,image/jpeg,image/webp"
                disabled={lasterOpp}
                onChange={(e) => {
                  const fil = e.target.files?.[0];
                  if (fil) void lastOpp(fil);
                  e.target.value = '';
                }}
              />
            </label>
          ) : null}

          <h2>Hvor dere er</h2>
          <div className="skjemarad">
            <Field label="Adresse" error={feltfeil?.fieldError('adresse')}>
              <input
                className="input"
                value={form.adresse}
                maxLength={200}
                onChange={(e) => sett('adresse', e.target.value)}
              />
            </Field>
            <Field label="Postnummer" error={feltfeil?.fieldError('postnummer')}>
              <input
                className="input"
                value={form.postnummer}
                maxLength={10}
                inputMode="numeric"
                onChange={(e) => sett('postnummer', e.target.value)}
              />
            </Field>
            <Field label="Poststed" error={feltfeil?.fieldError('poststed')}>
              <input
                className="input"
                value={form.poststed}
                maxLength={100}
                onChange={(e) => sett('poststed', e.target.value)}
              />
            </Field>
          </div>
          <p className="hint">Nåla på kartet settes fra adressen når dere lagrer.</p>

          <h2>Slik når brudeparene dere</h2>
          <Field
            label="Nettsted"
            hint="Med https:// foran."
            error={feltfeil?.fieldError('nettsted')}
          >
            <input
              className="input"
              type="url"
              value={form.nettsted}
              maxLength={300}
              placeholder="https://"
              onChange={(e) => sett('nettsted', e.target.value)}
            />
          </Field>
          <div className="skjemarad">
            <Field label="E-post" error={feltfeil?.fieldError('epost')}>
              <input
                className="input"
                type="email"
                value={form.epost}
                maxLength={200}
                onChange={(e) => sett('epost', e.target.value)}
              />
            </Field>
            <Field label="Telefon" error={feltfeil?.fieldError('telefon')}>
              <input
                className="input"
                type="tel"
                value={form.telefon}
                maxLength={40}
                onChange={(e) => sett('telefon', e.target.value)}
              />
            </Field>
          </div>
          <Field
            label="Pris fra (kr)"
            hint="Et anslag, ikke en pris. La feltet stå tomt om det ikke passer."
            error={feltfeil?.fieldError('prisFra')}
          >
            <input
              className="input"
              type="number"
              min={0}
              value={form.prisFra ?? ''}
              onChange={(e) => sett('prisFra', e.target.value ? Number(e.target.value) : null)}
            />
          </Field>

          {melding ? <Alert kind={melding.slag}>{melding.tekst}</Alert> : null}

          <div className="skjemaknapper">
            <button type="submit" className="btn btn-primary btn-lg" disabled={lagrer}>
              {lagrer ? 'Lagrer …' : 'Lagre oppføringen'}
            </button>
          </div>

          <p className="hint">
            Kategori: {data.oppforing.kategoriNavn}.{' '}
            {data.oppforing.publisert
              ? 'Oppføringen er publisert, og endringer vises med én gang.'
              : 'Oppføringen publiseres av Vår Dag når den er fylt ut.'}{' '}
            Spørsmål? Svar på e-posten dere fikk, eller skriv til post@vardag.no.
          </p>
        </form>
      </div>
    </div>
  );
}
